import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
import { SiteStaffMemberSchema, StaffRequestSchema } from "./staff-requests.js";
export const ClinicMessageRecipientSchema = z
    .object({
    kind: z.enum(["user", "team"]),
    id: IdSchema,
})
    .strict();
export const ClinicMessageSourceSchema = z
    .object({
    kind: z.enum([
        "result",
        "report",
        "referral",
        "document",
        "encounter",
        "historical_lab",
        "incoming_document",
        "clinic_document",
        "referral_package",
    ]),
    id: z.string().min(1).max(240),
    version: z.number().int().nonnegative().optional(),
    workflowVersion: z.number().int().positive().optional(),
    label: z.string().trim().min(1).max(240),
})
    .strict();
export const ClinicMessageTeamSchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    name: z.string().min(1),
    active: z.boolean(),
    version: z.number().int().positive(),
    members: z.array(IdSchema),
})
    .strict();
export const ClinicMessageDirectorySchema = z
    .object({
    siteId: IdSchema,
    enabled: z.boolean(),
    canAdmin: z.boolean(),
    people: z.array(SiteStaffMemberSchema),
    teams: z.array(ClinicMessageTeamSchema),
})
    .strict();
export const ClinicConversationEventSchema = z
    .object({
    sequence: z.number().int().positive(),
    kind: z.enum(["message", "participants", "work"]),
    authorId: IdSchema,
    authorName: z.string(),
    body: z.string(),
    createdAt: IsoDateTimeSchema,
})
    .strict();
export const ClinicConversationSchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema.nullable(),
    patientName: z.string().nullable(),
    subject: z.string(),
    createdBy: IdSchema,
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    completedAt: IsoDateTimeSchema.nullable().optional(),
    version: z.number().int().positive(),
    lastSequence: z.number().int().nonnegative(),
    preview: z.string(),
    bucket: z.enum(["action", "waiting", "completed"]),
    unread: z.boolean(),
    sentByMe: z.boolean(),
    canReply: z.boolean(),
    canManage: z.boolean(),
    historyOnly: z.boolean(),
    participants: z.array(ClinicMessageRecipientSchema.extend({ name: z.string(), active: z.boolean() })),
    people: z.array(z.object({ id: IdSchema, name: z.string(), active: z.boolean() }).strict()),
    sources: z.array(ClinicMessageSourceSchema.extend({ available: z.boolean() })),
    teams: z.array(z
        .object({
        id: IdSchema,
        name: z.string(),
        status: z.enum(["open", "in_progress", "completed"]),
        ownerId: IdSchema.nullable(),
        ownerName: z.string().nullable(),
        canClaim: z.boolean(),
        canComplete: z.boolean(),
    })
        .strict()),
    request: StaffRequestSchema.nullable(),
    needsReassignment: z.boolean(),
})
    .strict();
export const ClinicConversationDetailSchema = z
    .object({
    conversation: ClinicConversationSchema,
    events: z.array(ClinicConversationEventSchema),
    nextBeforeSequence: z.number().int().positive().nullable(),
})
    .strict();
export const ClinicConversationsSchema = z
    .object({
    siteId: IdSchema,
    items: z.array(ClinicConversationSchema),
    nextCursor: z.string().nullable(),
    counts: z
        .object({
        action: z.number().int(),
        waiting: z.number().int(),
        completed: z.number().int(),
        unread: z.number().int(),
    })
        .strict(),
})
    .strict();
export const ClinicConversationsQuerySchema = z
    .object({
    siteId: IdSchema,
    scope: z.enum(["mine", "teams", "sent", "patient", "reassignment"]).default("mine"),
    patientId: IdSchema.optional(),
    sourceKind: ClinicMessageSourceSchema.shape.kind.optional(),
    sourceId: z.string().min(1).max(240).optional(),
    bucket: z.enum(["action", "waiting", "completed"]).optional(),
    search: z.string().trim().max(200).default(""),
    cursor: z.string().max(160).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict()
    .refine((q) => (q.sourceKind === undefined) === (q.sourceId === undefined), "Select an attached record kind and ID together")
    .refine((q) => q.scope !== "patient" || q.patientId !== undefined, "Patient history requires a patient");
const recipients = z.array(ClinicMessageRecipientSchema).min(1).max(100);
export const CreateClinicConversationSchema = z
    .object({
    siteId: IdSchema,
    patientId: IdSchema.nullable().default(null),
    subject: z.string().trim().max(160).default(""),
    body: z.string().trim().min(1).max(2000),
    recipients,
    sources: z.array(ClinicMessageSourceSchema).max(20).default([]),
    idempotencyKey: IdSchema,
    request: z
        .object({
        encounterId: IdSchema.optional(),
        resultId: IdSchema.optional(),
        reportId: IdSchema.optional(),
        assignedTo: IdSchema.nullable(),
        dueAt: IsoDateTimeSchema.nullable(),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((c) => c.patientId !== null || c.sources.length === 0, "Attachments require a patient");
export const ClinicConversationSourceSchema = z
    .object({
    siteId: IdSchema,
    conversationId: IdSchema,
    kind: ClinicMessageSourceSchema.shape.kind,
    id: z.string(),
    title: z.string(),
    text: z.string(),
    hasFile: z.boolean(),
})
    .strict();
/** Returned only across the server/RPC boundary, never accepted as a caller supplied download path. */
export const ClinicMessageSourceFileSchema = z
    .object({
    tenantId: IdSchema,
    siteId: IdSchema,
    patientId: IdSchema,
    bucket: z.enum([
        "incoming-patient-documents",
        "patient-documents",
        "clinic-document-artifacts",
        "referral-artifacts",
    ]),
    objectName: z.string().min(1).max(1000),
    mimeType: z.enum(["application/pdf", "image/png", "image/jpeg"]),
    contentHash: Sha256Schema,
    byteSize: z.number().int().positive().max(250000000),
    fileName: z.string().max(240),
})
    .strict();
export const ClinicConversationCommandSchema = z.discriminatedUnion("action", [
    z.object({ action: z.literal("reply"), body: z.string().trim().min(1).max(2000) }).strict(),
    z
        .object({
        action: z.literal("participants"),
        add: z.array(ClinicMessageRecipientSchema).max(100).default([]),
        remove: z.array(ClinicMessageRecipientSchema).max(100).default([]),
        reassignTo: IdSchema.optional(),
        returnToTeam: IdSchema.optional(),
    })
        .strict(),
    z
        .object({
        action: z.enum(["read", "done", "waiting", "reopen"]),
        throughSequence: z.number().int().nonnegative(),
        fromSequence: z.number().int().positive().optional(),
    })
        .strict(),
    z.object({ action: z.enum(["claim", "team_done", "release"]), teamId: IdSchema }).strict(),
    z.object({ action: z.literal("complete_request") }).strict(),
    z.object({ action: z.literal("reassign"), userId: IdSchema }).strict(),
]);
export const ClinicConversationMutationSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    idempotencyKey: IdSchema,
    command: ClinicConversationCommandSchema,
})
    .strict();
export const SaveClinicMessageTeamSchema = z
    .object({
    siteId: IdSchema,
    id: IdSchema.optional(),
    expectedVersion: z.number().int().positive().optional(),
    name: z.string().trim().min(1).max(100),
    members: z.array(IdSchema).max(200),
    active: z.boolean().default(true),
    idempotencyKey: IdSchema,
})
    .strict();
//# sourceMappingURL=clinic-conversations.js.map